import { PERSONAS, type Persona } from "../app/personas.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";

/**
 * The front door (UX spec v3 §6.10, mock-up 09-signin.html): the assistant's
 * mark, one promise, the demo's customers as a radio group, one button that
 * names the person, and — once, under it — that this is a demo bank.
 *
 * No bank tag (nothing here comes from the ledger), no mic (nothing to say before
 * you are someone), no sign button. States: pick · signing-in · error ·
 * signed-out · busy (DCTA-185: every demo bank is in the middle of a signature — the bank's wait and Retry). Picking Mdm Lim turns the screen to large type on the spot; the
 * caller does that in `onPick`, because the root's `data-type` is not this
 * view's to own.
 */
export interface DoorHandlers {
  onPick(persona: Persona): void;
  onContinue(persona: Persona): void;
  onRetry(): void;
}

/**
 * `{ busy }` (DCTA-185): every demo bank is in the middle of a signature — the bank's wait, in whole seconds.
 * `unavailable` (P0-1, DCTA-192): the bank has no such customer — pick someone else; a retry would not help.
 */
export type DoorBanner = "error" | "signed-out" | "unavailable" | { readonly busy: number } | null;

/** The door's words for each banner that is a sentence (DCTA-192: the 404's line is the polish audit's, P0-1). */
const BANNER_TEXT = {
  error: "Can't reach the bank right now. Nothing has changed.",
  "signed-out": "You were signed out. Nothing was sent.",
  unavailable: "That customer isn't available right now. Pick someone else.",
} as const;
const BANNER_ICON = { error: "i-alert", "signed-out": "i-lock", unavailable: "i-user" } as const;

/** DCTA-185 (Claude's words, 12 Oct): calm, the bank's own number, never the raw refusal — the wait never breaks across lines. */
export function busyLine(seconds: number): readonly [string, string, string] {
  return ["The demo is busy — try again in ", `${String(seconds)} ${seconds === 1 ? "second" : "seconds"}`, "."];
}

/** "2 more customers" — one is "1 more customer". */
function moreCustomers(n: number): string {
  return `${String(n)} more ${n === 1 ? "customer" : "customers"}`;
}

export class Door {
  /** `.body` and `.dock`, in that order — the screen's two regions. */
  readonly parts: readonly HTMLElement[];
  #chosen: Persona;
  #busy = false;
  /** P0-1 (DCTA-192): the customers on offer — the bank's list (`offer`), every one until it answers. */
  #offered: readonly Persona[] = PERSONAS;
  readonly #radios: HTMLButtonElement[];
  readonly #more: HTMLButtonElement;
  readonly #moreText: Text;
  readonly #go: HTMLButtonElement;
  readonly #label: HTMLElement;
  readonly #banner: HTMLElement;
  readonly #bannerText: HTMLElement;
  readonly #bannerIcon: HTMLElement;
  readonly #retry: HTMLButtonElement;
  readonly #handlers: DoorHandlers;

  constructor(handlers: DoorHandlers, chosen: Persona = PERSONAS[0] as Persona, opts: { readonly installHint?: boolean } = {}) {
    this.#handlers = handlers;
    this.#chosen = chosen;

    this.#radios = PERSONAS.map((p) => {
      const radio = el(
        "button",
        { class: "raw persona", type: "button", role: "radio", data: { id: p.key } },
        [
          el("span", { class: "av", aria: { hidden: "true" } }, [p.initials]),
          el("span", { class: "tx" }, [el("b", {}, [p.title]), el("small", {}, [p.note])]),
          el("span", { class: "ck", aria: { hidden: "true" } }, [icon("i-check")]),
        ],
      );
      radio.addEventListener("click", () => this.#pick(p));
      radio.addEventListener("keydown", (event) => this.#arrow(event, p));
      return radio;
    });
    const first = this.#radios.filter((_, i) => !(PERSONAS[i] as Persona).more);
    const extra = this.#radios.filter((_, i) => (PERSONAS[i] as Persona).more);

    this.#moreText = document.createTextNode(moreCustomers(extra.length));
    this.#more = el("button", { class: "raw more", type: "button", aria: { expanded: "false" } }, [this.#moreText, icon("i-chev-d")]);
    this.#more.addEventListener("click", () => this.#expand(this.#more.getAttribute("aria-expanded") !== "true"));

    this.#bannerText = el("span", { class: "btxt" });
    this.#bannerIcon = el("span", { class: "bic", aria: { hidden: "true" } });
    this.#retry = el("button", { class: "raw retry", type: "button" }, ["Retry"]);
    // Under the busy rule like Continue: a double tap is one retry (its login or its health check is in flight).
    this.#retry.addEventListener("click", () => {
      if (this.#busy) return;
      this.#handlers.onRetry();
    });
    this.#banner = el("div", { class: "banner", role: "status", hidden: true }, [
      this.#bannerIcon,
      this.#bannerText,
      this.#retry,
    ]);

    this.#label = el("span", { class: "lbl" });
    this.#go = el("button", { class: "raw btn go", type: "button", aria: { busy: "false" } }, [this.#label]);
    this.#go.addEventListener("click", () => {
      if (this.#busy) return;
      this.#handlers.onContinue(this.#chosen);
    });

    const body = el("div", { class: "body" }, [
      this.#banner,
      el("div", { class: "door" }, [
        el("span", { class: "ai-mark", aria: { hidden: "true" } }),
        el("h1", {}, ["Your bank, in one sentence."]),
        // DCTA-217 (Umakhan, 13 Oct): the paragraph ends at the passkey — the slogan's "Nothing moves without it." is out.
        el("p", {}, ["Say what you want done. The bank shows exactly what will happen, and you sign that with your passkey."]),
        el("div", { class: "sec" }, [el("h3", {}, ["Who's signing in?"])]),
        el("div", { class: "list", role: "radiogroup", aria: { label: "Demo customer" } }, [
          ...first,
          this.#more,
          el("div", { class: "extra" }, extra),
        ]),
        // Research 11 item 11: iOS has no install prompt — one line says the way (copy: README D3).
        ...(opts.installHint === true ? [el("p", { class: "install" }, ["Use it full screen: Share → Add to Home Screen → Open as Web App."])] : []),
      ]),
      el("div", { class: "door-end", aria: { hidden: "true" } }),
    ]);
    const dock = el("div", { class: "dock" }, [
      el("div", { class: "cta" }, [this.#go]),
      el("div", { class: "demo" }, [icon("i-bank"), el("span", {}, ["Demo bank, demo money. Nothing here is real."])]),
    ]);
    this.parts = [body, dock];
    this.#paint();
  }

  get chosen(): Persona {
    return this.#chosen;
  }

  busy(on: boolean): void {
    this.#busy = on;
    this.#paint();
  }

  banner(kind: DoorBanner): void {
    this.#banner.hidden = kind === null;
    if (kind === null) return;
    const busy = typeof kind === "object";
    if (busy) {
      const [before, wait, after] = busyLine(kind.busy);
      this.#bannerText.replaceChildren(before, el("span", { class: "nw" }, [wait]), after);
    } else {
      this.#bannerText.textContent = BANNER_TEXT[kind];
    }
    this.#bannerIcon.replaceChildren(icon(busy ? "i-clock" : BANNER_ICON[kind]));
    this.#retry.hidden = !busy && kind !== "error";
  }

  /**
   * P0-1 (DCTA-192): only these customers are on offer (app/door-model.ts › offered: the door's rows the bank lists, in the
   * door's order; an empty list changes nothing): every other row goes, the disclosure counts what it holds (and goes when
   * it holds nobody), the arrow keys skip the rows that went. A choice the bank does not list hands over to the first
   * customer it does — reported as a pick, so the screen's type follows it.
   */
  offer(list: readonly Persona[]): void {
    const first = list[0];
    if (first === undefined) return;
    this.#offered = list;
    this.#radios.forEach((radio, i) => {
      radio.hidden = !list.includes(PERSONAS[i] as Persona);
    });
    const extra = list.filter((p) => p.more).length;
    this.#moreText.textContent = moreCustomers(extra);
    this.#more.hidden = extra === 0;
    const lost = !list.includes(this.#chosen);
    if (lost) this.#chosen = first;
    this.#paint();
    if (lost) this.#handlers.onPick(first);
  }

  focusChoice(): void {
    this.#radios[PERSONAS.indexOf(this.#chosen)]?.focus();
  }

  #pick(persona: Persona): void {
    if (this.#busy) return;
    this.#chosen = persona;
    if (persona.more) this.#expand(true);
    this.#paint();
    this.#handlers.onPick(persona);
  }

  #arrow(event: KeyboardEvent, from: Persona): void {
    const step = event.key === "ArrowDown" || event.key === "ArrowRight" ? 1 : event.key === "ArrowUp" || event.key === "ArrowLeft" ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const open = this.#more.getAttribute("aria-expanded") === "true";
    const reachable = this.#offered.filter((p) => open || !p.more);
    const at = reachable.indexOf(from);
    const next = reachable[(at + step + reachable.length) % reachable.length];
    if (next === undefined) return;
    this.#pick(next);
    this.#radios[PERSONAS.indexOf(next)]?.focus();
  }

  #expand(open: boolean): void {
    this.#more.setAttribute("aria-expanded", open ? "true" : "false");
  }

  #paint(): void {
    this.#radios.forEach((radio, i) => {
      const on = PERSONAS[i] === this.#chosen;
      radio.setAttribute("aria-checked", on ? "true" : "false");
      radio.setAttribute("tabindex", on ? "0" : "-1");
      radio.setAttribute("aria-disabled", this.#busy ? "true" : "false");
    });
    this.#go.setAttribute("aria-busy", this.#busy ? "true" : "false");
    this.#go.setAttribute("aria-disabled", this.#busy ? "true" : "false");
    this.#label.textContent = `Continue as ${this.#chosen.name}`;
  }
}
